import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Space,
  Spin,
  Switch,
} from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useFirestore } from "react-redux-firebase";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import urls from "urls";
import { FIRESTORE_CAMPAIGNS_TABLE } from "constants/FirestoreConstant";
import _ from "lodash";

const { Option } = Select;
const { TextArea } = Input;

const CampaignForm = ({ isEdit = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const firestore = useFirestore();
  const campaigns = useSelector((state) => state.firestoreReducer.ordered.campaigns);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isOngoing, setIsOngoing] = useState(false);

  const clickedCampaign = React.useMemo(() => _.find(campaigns, ["id", id]) || {}, [campaigns, id]);

  useEffect(() => {
    if (isEdit && id && clickedCampaign.id) {
      const convertToDate = (dateValue) => {
        if (!dateValue) return null;
        // Check if it's a Firestore Timestamp
        if (dateValue.toDate && typeof dateValue.toDate === "function") {
          return dayjs(dateValue.toDate());
        }
        // Check if it's already a Date object
        if (dateValue instanceof Date) {
          return dayjs(dateValue);
        }
        // Try to parse as string/timestamp
        return dayjs(dateValue);
      };

      const formData = {
        ...clickedCampaign,
        startDate: convertToDate(clickedCampaign.startDate),
        endDate: convertToDate(clickedCampaign.endDate),
      };
      form.setFieldsValue(formData);
      setIsOngoing(clickedCampaign.isOngoing || false);
    }
  }, [form, isEdit, id, clickedCampaign]);

  // Load UTM data from location state if available
  useEffect(() => {
    if (!isEdit && location.state?.utmData) {
      const utmData = location.state.utmData;
      form.setFieldsValue({
        utmSource: utmData.utmSource,
        utmMedium: utmData.utmMedium,
        utmCampaign: utmData.utmCampaign,
        utmTerm: utmData.utmTerm,
        utmContent: utmData.utmContent,
      });
    }
  }, [form, isEdit, location.state]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const campaignData = {
        ...values,
        startDate: values.startDate ? values.startDate.toDate() : null,
        endDate: isOngoing ? null : values.endDate ? values.endDate.toDate() : null,
        isOngoing: isOngoing,
        updatedAt: firestore.FieldValue.serverTimestamp(),
      };

      if (isEdit) {
        await firestore.collection(FIRESTORE_CAMPAIGNS_TABLE).doc(id).update(campaignData);
        message.success(t("campaign_updated"));
      } else {
        // Let Firestore auto-generate the document ID
        await firestore.collection(FIRESTORE_CAMPAIGNS_TABLE).add({
          ...campaignData,
          createdAt: firestore.FieldValue.serverTimestamp(),
        });
        message.success(t("campaign_created"));
      }

      navigate(urls.campaigns);
    } catch (error) {
      console.error("Error saving campaign:", error);
      message.error(t("notifications.error_saving_campaign"));
    } finally {
      setLoading(false);
    }
  };

  const handleOngoingChange = (checked) => {
    setIsOngoing(checked);
    if (checked) {
      form.setFieldsValue({ endDate: null });
    }
  };

  // Show loading if editing but campaign not loaded yet
  if (isEdit && (!campaigns || !clickedCampaign.id)) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(urls.campaigns)}>
              {t("back")}
            </Button>
            <h2>{isEdit ? t("edit_campaign") : t("add_campaign")}</h2>
          </Space>
        </Col>
      </Row>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: "draft",
            platform: "facebook_ads",
            isOngoing: false,
          }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("campaign_name")}
                name="name"
                rules={[{ required: true, message: t("please_enter_campaign_name") }]}
              >
                <Input placeholder={t("enter_campaign_name")} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("platform")}
                name="platform"
                rules={[{ required: true, message: t("please_select_platform") }]}
              >
                <Select placeholder={t("select_platform")}>
                  <Option value="facebook_ads">{t("facebook_ads")}</Option>
                  <Option value="google_ads">{t("google_ads")}</Option>
                  <Option value="instagram_ads">{t("instagram_ads")}</Option>
                  <Option value="email_marketing">{t("email_marketing")}</Option>
                  <Option value="other">{t("other")}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={t("campaign_description")} name="description">
            <TextArea rows={4} placeholder={t("enter_campaign_description")} />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={8}>
              <Form.Item
                label={t("start_date")}
                name="startDate"
                rules={[{ required: true, message: t("please_select_start_date") }]}
              >
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={t("ongoing")}>
                <Switch
                  checked={isOngoing}
                  onChange={handleOngoingChange}
                  checkedChildren={t("ongoing")}
                  unCheckedChildren={t("end_date")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              {!isOngoing && (
                <Form.Item
                  label={t("end_date")}
                  name="endDate"
                  rules={[
                    { required: !isOngoing, message: t("please_select_end_date") },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || !getFieldValue("startDate")) {
                          return Promise.resolve();
                        }
                        if (value.isBefore(getFieldValue("startDate"))) {
                          return Promise.reject(new Error(t("end_date_must_be_after_start_date")));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={6}>
              <Form.Item label={t("budget")} name="budget">
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder={t("enter_budget")}
                  min={0}
                  step={10}
                  formatter={(value) => `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/€\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label={t("status")}
                name="status"
                rules={[{ required: true, message: t("please_select_status") }]}
              >
                <Select placeholder={t("select_status")}>
                  <Option value="draft">{t("draft")}</Option>
                  <Option value="active">{t("active")}</Option>
                  <Option value="paused">{t("paused")}</Option>
                  <Option value="completed">{t("completed")}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={t("objectives")} name="objectives">
            <TextArea rows={3} placeholder={t("enter_campaign_objectives")} />
          </Form.Item>

          <Card title={t("utm_parameters")} size="small" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t("utm_source")}
                  name="utmSource"
                  rules={[{ required: true, message: t("form.please_enter_utm_source") }]}
                >
                  <Input placeholder="facebook, google, newsletter" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t("utm_medium")}
                  name="utmMedium"
                  rules={[{ required: true, message: t("form.please_enter_utm_medium") }]}
                >
                  <Input placeholder="cpc, social, email" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t("utm_campaign")}
                  name="utmCampaign"
                  rules={[{ required: true, message: t("form.please_enter_utm_campaign") }]}
                >
                  <Input placeholder="spring_sale, product_launch" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t("utm_term")}
                  name="utmTerm"
                  rules={[{ required: true, message: t("form.please_enter_utm_term") }]}
                >
                  <Input placeholder="keyword targeting" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t("utm_content")}
                  name="utmContent"
                  rules={[{ required: true, message: t("please_enter_utm_content") }]}
                >
                  <Input placeholder="ad_variation, button_color" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                {isEdit ? t("update_campaign") : t("create_campaign")}
              </Button>
              <Button onClick={() => navigate(urls.campaigns)}>{t("cancel")}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CampaignForm;
